import React from "react";
import Login from "./pages/user/Login.jsx";
import Dashboard from "./pages/dashboard/Dashboard.jsx";
import SignUp from "./pages/user/SignUp.js";
import PasswordReset from "./pages/user/PasswordReset";
import NewPassword from "./pages/user/NewPassword";
import Users from "./pages/users";
import Groups from "./pages/groups";
import Timesheets from "./pages/timesheets/";
import Clients from "./pages/clients";
import Analytics from "./pages/analytics/Analytics.js";
import PhoneReset from "./pages/user/PhoneReset.js";
import Invoice from "./pages/invoice";
import Bugs from "./pages/bugs/index.jsx";
import BugDetails from "./pages/bugs/BugDetails";
import Logout from "pages/dashboard/Logout.js";
import Faq from "./pages/tutorial/Faq";
import {
  BrowserRouter as Router,
  Route,
  Redirect,
  Switch,
  matchPath,
  useLocation,
} from "react-router-dom";
import cookie from "js-cookie";
import axios from "axios";
import { clearCookies } from "globalResources/helpers/index.js";
import { NavigationBar } from "./globalComponents";
import { setUser } from "store/slices/userSlice.js";
import HandleLoginLink from "pages/user/HandleLoginLink.jsx";
import BatchEntries from "pages/timesheets/BatchEntries.js";
import ExternalTimeStation from "pages/external/ExternalTimeStation.jsx";
import ClientDetails from "pages/clients/ClientDetails.jsx";
import RateEditor from "pages/timesheets/components/RateEditor.jsx";
import TimeEntryImport from "pages/timesheets/TimeEntryImport.jsx";
import ViewReport from "pages/accounting/components/ViewReport.js";
import Notification from "globalComponents/Notification.jsx";
import UserDetails from "pages/users/UserDetails.jsx";
import GroupDetails from "pages/groups/GroupDetails.jsx";
import UserImport from "pages/users/UserImport.jsx";
import DownloadPage from "pages/accounting/DownloadPage.jsx";
import Payroll from "pages/accounting/payroll/index.jsx";
import PayrollManagement from "pages/accounting/payroll/PayrollManagement.jsx";
import ViewPayrollReport from "pages/accounting/payroll/ViewPayrollReport.jsx";
import ReportList from "pages/accounting/components/ReportList.js";
import Adjustment from "pages/accounting/adjustments";
import Home from "pages/home/index.jsx";
import Contact from "pages/home/contact.jsx";
import Privacy from "pages/home/privacy.jsx";
import CookiePolicyPopup from "globalComponents/popups/CookiePolicyPopup.jsx";
import CookiePolicy from "pages/home/cookie.jsx";
import PrivacyPolicyPopup from "globalComponents/popups/PrivacyPolicyPopup.jsx";
import RootUser from "pages/onboarding/RootUser.jsx";
import OptIn from "pages/home/optin.jsx";
import Password from "pages/onboarding/Password.jsx";
import Account from "pages/onboarding/Account.jsx";
import Onboarding from "pages/onboarding/index.jsx";
import Company from "pages/company/index.jsx";
import { AccountingHome } from "globalComponents/AccountingFolderContainer";
import TermsAndConditions from "pages/home/termsAndConditions.jsx";
import UserSettings from "pages/user/UserSettings.jsx";
import HandleShortLink from "pages/user/HandleShortLink.jsx";
import NotificationsPage from "pages/notifications/index.jsx";

// Pages without the navigation bar (exact matches; :params allowed).
const NO_NAV_BAR_ROUTES = [
  "/login",
  "/signup",
  "/password",
  "/phoneReset",
  "/user/password",
  "/login/link/:token",
  "/timestation",
  "/report/link",
  "/onboarding",
  "/onboarding/account",
  "/onboarding/password",
  "/",
  "/faq",
  "/invoice", // Add any other routes that should not show the navigation bar here
];

// Lives inside the Router so it re-renders on every navigation. App itself
// doesn't, so reading window.location there left the bar hidden after going
// from the home page (/) to the dashboard until a full reload.
const NavigationBarForPage = () => {
  const { pathname } = useLocation();
  const hidden = NO_NAV_BAR_ROUTES.some((path) =>
    matchPath(pathname, { path, exact: true }),
  );
  if (!cookie.get("token") || hidden) return null;
  return <NavigationBar />;
};

class App extends React.Component {
  render() {
    let token = cookie.get("token");
    if (!token) {
      clearCookies(true);
      setUser({});
    }

    axios.defaults.headers.common["Authorization"] = `Bearer ${cookie.get(
      "token",
    )}`;

    axios.defaults.baseURL = process.env.REACT_APP_API_URL;

    return (
      <Router>
        <Notification />
        <CookiePolicyPopup />
        <PrivacyPolicyPopup />
        <NavigationBarForPage />
        {token ? (
          <Switch>
            <Route path="/bugs" component={Bugs} />
            <Route path="/bug/:id/details" component={BugDetails} />
            <Route path="/faq" component={Faq} />
            <Route path="/invoice" component={Invoice} />
            <Route exact path="/dashboard" component={Dashboard} />
            <Route exact path="/users" component={Users} />
            <Route exact path="/notifications" component={NotificationsPage} />
            <Route path="/user/:id/details" component={UserDetails} />
            <Route path="/users/import" component={UserImport} />
            <Route path="/groups" component={Groups} />
            <Route path="/group/:id/details" component={GroupDetails} />
            <Route exact path="/timesheets" component={Timesheets} />
            <Route path="/clients" component={Clients} />
            <Route path="/client/:id/details" component={ClientDetails} />
            <Route path="/accounting/report/:id" component={ViewReport} />
            <Route path="/accounting/adjustment/:id" component={ViewReport} />
            <Route
              path="/accounting/payroll/run"
              component={PayrollManagement}
            />
            <Route
              exact
              path="/accounting/payroll/:id"
              component={ViewPayrollReport}
            />
            <Route path="/accounting/adjustment" component={Adjustment} />
            <Route exact path="/accounting/payroll" component={Payroll} />
            <Route path="/accounting/billing" component={ReportList} />
            <Route exact path="/accounting" component={AccountingHome} />
            <Route path="/analytics" component={Analytics} />
            <Route path="/timesheets/batch" component={BatchEntries} />
            <Route path="/timesheets/import" component={TimeEntryImport} />
            <Route path="/timesheets/rate-editor" component={RateEditor} />
            <Route path="/timestation" component={ExternalTimeStation} />
            <Route path="/report/link" component={DownloadPage} />
            <Route path="/logout" component={Logout} />
            <Route path="/contact" component={Contact} />
            <Route path="/privacy" component={Privacy} />
            <Route path="/terms" component={TermsAndConditions} />
            <Route path="/messaging" component={OptIn} />
            <Route path="/cookies" component={CookiePolicy} />
            <Route path="/user/password" component={Password} />
            <Route path="/" exact component={Home} />
            <Route path="/onboarding/root" component={RootUser} />
            <Route exact path="/onboarding" component={Onboarding} />
            <Route path="/onboarding/password" component={Password} />
            <Route path="/onboarding/account" component={Account} />
            <Route path="/company" component={Company} />
            <Redirect path="/join-requests" to="/company?tab=requests" />
            <Route path="/settings" component={UserSettings} />
            <Route path="/login/link/:token" component={HandleLoginLink} />
            <Redirect to="/dashboard" />
          </Switch>
        ) : (
          <Switch>
            <Route path="/faq" component={Faq} />
            <Route path="/invoice" component={Invoice} />
            <Route path="/login" exact component={Login} />
            <Route path="/signup" component={SignUp} />
            <Route path="/phoneReset" component={PhoneReset} />
            <Route path="/user/password" component={Password} />
            <Route path="/login/link/:token" component={HandleLoginLink} />
            <Route path="/link/:id" component={HandleShortLink} />
            <Route path="/timestation" component={ExternalTimeStation} />
            <Route path="/report/link" component={DownloadPage} />
            <Route path="/contact" component={Contact} />
            <Route path="/privacy" component={Privacy} />
            <Route path="/terms" component={TermsAndConditions} />
            <Route path="/messaging" component={OptIn} />
            <Route path="/cookies" component={CookiePolicy} />
            <Route path="/onboarding/root" component={RootUser} />
            <Route path="/" component={Home} />
            <Redirect to="/login" />
          </Switch>
        )}
      </Router>
    );
  }
}

export default App;
